import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import mainStyle from "../mainStyle.module.scss";
import { useOutletContext } from "react-router-dom";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { Card, Box, Tooltip, Divider, CircularProgress } from "@mui/material";
import { ErrorNotification } from "pages/System/UserManagement/ErrorHandle";

// assets
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { ReactComponent as AvatarNoneIcon } from "assets/images/icons/img_photo_none.svg";

import { useTheme } from "@mui/material/styles";

import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import DrawerField from "components/Drawer";

import EditMemberUser from "./Drawer/EditUser/Member";
import EditInvitation from "./Drawer/EditUser/Invitation";
import DeleteUserModal from "./Modal/DeleteUser";
import ResendModal from "./Modal/Resend";
import InviteUserModal from "./Modal/InviteUser";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const MspUserManagement = ({ entId }) => {
  const theme = useTheme();
  const { scopeReady, scopeData } = useOutletContext();
  const [userEnterpriseData, setUserEnterpriseData] = useState([]);
  const [userEnterprisePendingData, setUserEnterprisePendingData] = useState(
    []
  );
  const [combinedUserData, setCombinedUserData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);
  const [openResendModal, setOpenResendModal] = useState(false);
  const [openInviteUserModal, setOpenInviteUserModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageHandleModal, setErrorMessageHandleModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const stored = localStorage.getItem("selectBreadcrumbs");
  const [originalRowData, setOriginalRowData] = useState(null);
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();
  const [currentUserMspRoleName, setCurrentUserMspRoleName] = useState(
    JSON.parse(stored)?.realm?.roleName ?? null
  );
  const [currentUserMspRoleId, setCurrentUserMspRoleId] = useState(
    JSON.parse(stored)?.realm?.roleId ?? null
  );
  const isViewer =
    currentUserMspRoleName === "None" || currentUserMspRoleName === "Viewer"; // 如果是viewer的話就要隱藏

  const mspRoleHierarchy = [
    { mspRoleId: 1, mspRoleName: "Super Admin" },
    { mspRoleId: 2, mspRoleName: "Admin" },
    { mspRoleId: 3, mspRoleName: "Viewer" },
  ];

  const orgRoleHierarchy = [
    { orgRoleId: 5, orgRoleName: "Super Admin" },
    { orgRoleId: 6, orgRoleName: "Admin" },
    { orgRoleId: 7, orgRoleName: "Viewer" },
  ];

  const [listEnterpriseUsersUuid, setListEnterpriseUsersUuid] = useState(
    uuidv4()
  );

  const [listPendingEnterpriseUsersUuid, setListPendingEnterpriseUsersUuid] =
    useState(uuidv4());

  // List Enterprise Users
  const getListEnterpriseUsers = () => {
    sendRequest("/ents/:entId/users.read", listEnterpriseUsersUuid, { entId });
  };

  // List Pending Enterprise Users
  const getListPendingEnterpriseUsers = () => {
    sendRequest(
      "/ents/:entId/invitations.read",
      listPendingEnterpriseUsersUuid,
      { entId }
    );
  };

  useEffect(() => {
    if (checkMockup === "true" || scopeReady) {
      getListEnterpriseUsers();
      /* getListPendingEnterpriseUsers(); */
    }
  }, [checkMockup, scopeReady]);

  useEffect(() => {
    const mspUsersResponse = getData(listEnterpriseUsersUuid);

    if (mspUsersResponse) {
      setUserEnterpriseData(mspUsersResponse || []);
      getListPendingEnterpriseUsers();
    }
  }, [dataMap]);

  useEffect(() => {
    const pendingUsersResponse = getData(listPendingEnterpriseUsersUuid);

    if (pendingUsersResponse) {
      setUserEnterprisePendingData(pendingUsersResponse || []);
      setIsLoading(false);
    }
  }, [dataMap]);

  const isPendingStatus = (timeOfInvitation) => {
    if (!timeOfInvitation) return "-";
    const now = Math.floor(Date.now() / 1000);
    const sevenDaysInSeconds = 7 * 24 * 60 * 60;
    return now - timeOfInvitation <= sevenDaysInSeconds ? "pending" : "expired";
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const users = userEnterpriseData?.data?.users || [];
    const pending = userEnterprisePendingData?.data?.invitations || [];

    const combined = [
      ...users,
      ...pending.map((invitation) => ({
        ...invitation,
        status: isPendingStatus(invitation.timeOfInvitation),
      })),
    ];

    const filtered = combined.filter(
      (user) => user.mspRoleName && user.mspRoleName !== "None"
    );

    setCombinedUserData(filtered);
  }, [isLoading, userEnterpriseData, userEnterprisePendingData]);

  const formatStatus = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      case "expired":
        return "Expired";
      case "pending":
        return "Pending";
      default:
        return status;
    }
  };

  // 所有org list
  const allOrgNames = useMemo(() => {
    const orgs = (scopeData.orgs ?? []).map((org) => ({
      orgId: org.id,
      orgName: org.name,
      orgRoleId: org.roleId,
      orgRoleName: org.roleName,
    }));

    return orgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
  }, [scopeData.orgs]);

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const handleOpenResendModal = (rowIndex, rowData) => {
    setOpenResendModal(true);
    if (rowData) {
      setSelectedRowData(rowData);
    }
  };

  const handleOpenInviteUserModal = () => {
    setOpenInviteUserModal(true);
  };

  const handleCloseDeleteAccountModal = () => {
    setOpenDeleteAccountModal(false);
  };

  const handleCloseResendModal = () => {
    setOpenResendModal(false);
  };

  const handleCloseInviteUserModal = () => {
    setOpenInviteUserModal(false);
  };

  const handleDrawerClose = () => {
    setSelectedRowData({});
    setDrawerOpen(false);
  };

  const columnHelper = createColumnHelper();

  const columnsData = [
    columnHelper.accessor("name", {
      header: "Name",
      meta: {
        withRightBorder: true,
      },
      cell: (info) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {info.row.original.avatar ? (
            <img
              src={info.row.original.avatar}
              alt="User"
              className={mainStyle["avatar-with-photo"]}
            />
          ) : info.getValue() ? (
            <div className={mainStyle["avatar-without-photo-has-name"]}>
              {getInitials(info.getValue())}
            </div>
          ) : (
            <div className={mainStyle["avatar-without-photo"]}>
              <AvatarNoneIcon />
            </div>
          )}
          {info.getValue() ? <span>{info.getValue()}</span> : "-"}
        </Box>
      ),
    }),
    columnHelper.accessor("email", {
      header: "Account",
    }),
    columnHelper.accessor("status", {
      header: "Account Status",
      cell: (info) => (
        <span
          style={{
            color:
              info.getValue() === "pending" || info.getValue() === "expired"
                ? theme.palette.primary.redPrimary
                : info.getValue() === "active"
                  ? theme.palette.primary.greenPrimary
                  : info.getValue() === "inactive"
                    ? theme.palette.primary.greySecound
                    : theme.palette.primary.greyPrimary,
            border: `1.5px solid ${info.getValue() === "pending" || info.getValue() === "expired"
                ? theme.palette.primary.redPrimary
                : info.getValue() === "active"
                  ? theme.palette.primary.greenPrimary
                  : info.getValue() === "inactive"
                    ? theme.palette.primary.greySecound
                    : theme.palette.primary.greyPrimary
              }`,
            padding: "4px 12px",
            width: "91px",
            height: "27px",
            gap: "10px",
            borderRadius: theme.shape.customBorderRadius[7],
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            display: "flex",
          }}
        >
          {info.getValue() === undefined ? "-" : formatStatus(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("mspRoleName", {
      header: "MSP Role",
    }),

    columnHelper.accessor("orgAccesses", {
      header: "Organization Privilege",
      cell: (info) => {
        return (
          <Box>
            {info.row.original.mspRoleName === "Super Admin" ||
              info.row.original.mspRoleName === "Admin" ? (
              <div>All / {info.row.original.mspRoleName}</div>
            ) : info.getValue()?.length > 0 ? (
              info.getValue().length > 3 ? (
                <Tooltip
                  title={
                    <div>
                      {info.getValue().map((s, index) => (
                        <div
                          key={index}
                        >{`${s.orgName} / ${s.orgRoleName}`}</div>
                      ))}
                    </div>
                  }
                  placement="right"
                  arrow
                >
                  <div>
                    {info
                      .getValue()
                      .slice(0, 3)
                      .map((s, index) => (
                        <div
                          key={index}
                        >{`${s.orgName} / ${s.orgRoleName}`}</div>
                      ))}
                    <div>...</div>
                  </div>
                </Tooltip>
              ) : (
                <div>
                  {info.getValue().map((s, index) => (
                    <div key={index}>{`${s.orgName} / ${s.orgRoleName}`}</div>
                  ))}
                </div>
              )
            ) : (
              "-"
            )}
          </Box>
        );
      },
    }),
    columnHelper.accessor("lastLoginTime", {
      header: "Last Login",
      cell: (info) => (
        <span>
          {info.getValue()
            ? dayjs.unix(info.getValue()).format("YYYY/MM/DD HH:mm:ss")
            : "-"}
        </span>
      ),
    }),
    columnHelper.accessor("timeOfAuth", {
      header: "Joined On",
      cell: (info) => (
        <span>
          {dayjs
            .unix(info.getValue() || info.row.original.timeOfInvitation)
            .format("YYYY/MM/DD HH:mm:ss")}
        </span>
      ),
    }),
  ];

  const table = useTableInstance({
    tableName: "mspUserManagement",
    data: combinedUserData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  return (
    <>
      <Card className={mainStyle["card"]}>
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "10px 20px",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: theme.palette.primary.greyThread,
              minHeight: "50px",
              marginRight: "10px",
            }}
          />
          <Box
            sx={{
              display: "flex",
              padding: "3px 10px 3px",
              marginRight: "5px",
            }}
          >
            <SearchInput
              value={table.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </Box>
          {!isViewer && (
            <SolidButton
              startIcon={<AddIcon />}
              width="120px"
              height="32px"
              onClick={handleOpenInviteUserModal}
            >
              Invite User
            </SolidButton>
          )}
        </Box>
        <Box style={{ padding: "10px 20px", minHeight: "400px" }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "400px",
              }}
            >
              <CircularProgress
                sx={{ color: theme.palette.primary.mainHover }}
              />
            </Box>
          ) : (
            <Box margin="20px 0">
              <TableField
                {...table}
                drawerOpen={drawerOpen}
                handleRowOnClick={(row) => {
                  setOriginalRowData(row);
                  setSelectedRowData(row);
                  setDrawerOpen(true);
                }}
              />
              {drawerOpen && (
                <>
                  {selectedRowData?.status === "pending" ||
                    selectedRowData?.status === "expired" ? (
                    <>
                      <DrawerField
                        open={drawerOpen}
                        handleClose={handleDrawerClose}
                        contentKey={`MspUserInvitation_${uuidv4()}`}
                      >
                        <EditInvitation
                          setSelectedRowData={setSelectedRowData}
                          selectedRowData={selectedRowData}
                          userEnterprisePendingData={userEnterprisePendingData}
                          setUserEnterprisePendingData={
                            setUserEnterprisePendingData
                          }
                          drawerOpen={drawerOpen}
                          setDrawerOpen={setDrawerOpen}
                          getInitials={getInitials}
                          setOpenDeleteAccountModal={setOpenDeleteAccountModal}
                          handleOpenResendModal={handleOpenResendModal}
                          allOrgNames={allOrgNames}
                          orgRoleHierarchy={orgRoleHierarchy}
                          mspRoleHierarchy={mspRoleHierarchy}
                          entId={entId}
                          getListEnterpriseUsers={getListEnterpriseUsers}
                          getListPendingEnterpriseUsers={
                            getListPendingEnterpriseUsers
                          }
                          currentUserMspRoleId={currentUserMspRoleId}
                          currentUserMspRoleName={currentUserMspRoleName}
                          isViewer={isViewer}
                          formatStatus={formatStatus}
                          originalRowData={originalRowData}
                        />
                      </DrawerField>
                    </>
                  ) : (
                    <>
                      {drawerOpen && (
                        <DrawerField
                          open={drawerOpen}
                          handleClose={handleDrawerClose}
                          contentKey={`OrgUser_${uuidv4()}`}
                        >
                          <EditMemberUser
                            setSelectedRowData={setSelectedRowData}
                            selectedRowData={selectedRowData}
                            userEnterpriseData={userEnterpriseData}
                            setUserEnterpriseData={setUserEnterpriseData}
                            drawerOpen={drawerOpen}
                            setDrawerOpen={setDrawerOpen}
                            getInitials={getInitials}
                            setOpenDeleteAccountModal={
                              setOpenDeleteAccountModal
                            }
                            allOrgNames={allOrgNames}
                            orgRoleHierarchy={orgRoleHierarchy}
                            mspRoleHierarchy={mspRoleHierarchy}
                            entId={entId}
                            getListEnterpriseUsers={getListEnterpriseUsers}
                            getListPendingEnterpriseUsers={
                              getListPendingEnterpriseUsers
                            }
                            currentUserMspRoleId={currentUserMspRoleId}
                            setCurrentUserMspRoleId={setCurrentUserMspRoleId}
                            currentUserMspRoleName={currentUserMspRoleName}
                            isViewer={isViewer}
                            setCurrentUserMspRoleName={
                              setCurrentUserMspRoleName
                            }
                            formatStatus={formatStatus}
                            originalRowData={originalRowData}
                          />
                        </DrawerField>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Card>

      <DeleteUserModal
        openDeleteAccountModal={openDeleteAccountModal}
        handleCloseDeleteAccountModal={handleCloseDeleteAccountModal}
        selectedRowData={selectedRowData}
        entId={entId}
        getListEnterpriseUsers={getListEnterpriseUsers}
        getListPendingEnterpriseUsers={getListPendingEnterpriseUsers}
        setDrawerOpen={setDrawerOpen}
      />
      <ResendModal
        openResendModal={openResendModal}
        handleCloseResendModal={handleCloseResendModal}
        selectedRowData={selectedRowData}
        entId={entId}
        getListEnterpriseUsers={getListEnterpriseUsers}
        getListPendingEnterpriseUsers={getListPendingEnterpriseUsers}
      />
      <InviteUserModal
        openInviteUserModal={openInviteUserModal}
        setOpenInviteUserModal={setOpenInviteUserModal}
        handleCloseInviteUserModal={handleCloseInviteUserModal}
        allOrgNames={allOrgNames}
        orgRoleHierarchy={orgRoleHierarchy}
        mspRoleHierarchy={mspRoleHierarchy}
        entId={entId}
        currentUserMspRoleId={currentUserMspRoleId}
        getListEnterpriseUsers={getListEnterpriseUsers}
        getListPendingEnterpriseUsers={getListPendingEnterpriseUsers}
      />

      <ErrorNotification
        open={errorMessageHandleModal}
        onClose={() => setErrorMessageHandleModal(false)}
        message={errorMessage}
      />
    </>
  );
};

export default MspUserManagement;
